/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  CircleCheck, CircleX, ExternalLink, Instagram, Loader2, Music2, RefreshCw, ShieldAlert, Video, Youtube,
} from 'lucide-react';
import { User as UserEntity } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { earnCreditApi } from '@/lib/earnCreditApi';
import { BRAND_PRIMARY_BTN_CLASS } from '@/constants/brandColors';
import { EARN_CREDIT_DECLINE_REASONS, EARN_CREDIT_REVIEW_WINDOW, formatEarnAmount } from '@/constants/earnCreditProgram';

/**
 * /earn-review: the team's queue for Earn Defera Credit (server/earn-credit.js).
 * Approve adds the credit through the same grant as the admin credit form
 * (the customer gets the "Defera Credit added" email); Decline sends the
 * customer the reason picked or written here. Admin only: approving grants
 * credit, which the concierge role can't. The server checks; the role check
 * here only decides what to render.
 */

function isCurrentUserAdmin(u) {
  if (!u) return false;
  const roles = u?.app_metadata?.roles || u?.appMetadata?.roles || [];
  return (Array.isArray(roles) && roles.includes('admin')) || u?.role === 'admin';
}

const TABS = [
  { status: 'pending', label: 'Waiting' },
  { status: 'approved', label: 'Approved' },
  { status: 'rejected', label: 'Declined' },
  { status: 'withdrawn', label: 'Withdrawn' },
];
const PLATFORM_ICONS = { TikTok: Music2, Instagram, YouTube: Youtube };
const CARD = 'rounded-2xl border border-white/10 bg-white/[0.03]';

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

function DueBadge({ dueAt, now }) {
  if (!dueAt) return null;
  const hours = Math.round((Date.parse(dueAt) - now) / 3600000);
  const overdue = hours < 0;
  const soon = !overdue && hours <= 8;
  return (
    <span className={cn(
      'rounded-full border px-2 py-0.5 text-[11px] whitespace-nowrap',
      overdue ? 'border-rose-400/30 bg-rose-400/10 text-rose-200'
        : soon ? 'border-amber-400/30 bg-amber-400/10 text-amber-200'
          : 'border-white/15 bg-white/5 text-white/60',
    )}>
      {overdue ? `${Math.abs(hours)}h overdue` : hours < 1 ? 'Due within the hour' : `Due in ${hours}h`}
    </span>
  );
}

function DeclinePicker({ onCancel, onConfirm, busy }) {
  const [choice, setChoice] = useState(EARN_CREDIT_DECLINE_REASONS[0]);
  const [custom, setCustom] = useState('');
  const reason = choice === 'custom' ? custom.trim() : choice;
  return (
    <div className="mt-3 space-y-2.5 rounded-xl border border-rose-400/20 bg-rose-400/[0.04] p-3.5">
      <p className="text-[12px] text-white/60">The customer reads this on /earn and in the email.</p>
      {[...EARN_CREDIT_DECLINE_REASONS, 'custom'].map((r) => (
        <label key={r} className="flex cursor-pointer items-start gap-2.5 text-[13px] text-white/80">
          <input type="radio" name="decline-reason" checked={choice === r} onChange={() => setChoice(r)} className="mt-1 accent-rose-400" />
          <span>{r === 'custom' ? 'Write my own' : r}</span>
        </label>
      ))}
      {choice === 'custom' && (
        <Textarea value={custom} onChange={(e) => setCustom(e.target.value.slice(0, 500))} rows={2} placeholder="Written to the customer" className="rounded-lg border-white/15 bg-black/30 text-sm text-white" />
      )}
      <div className="flex gap-2 pt-1">
        <Button type="button" size="sm" disabled={busy || reason.length < 5} onClick={() => onConfirm(reason)} className="rounded-lg bg-rose-500/80 text-white hover:bg-rose-500">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Decline and email them'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} className="rounded-lg text-white/60 hover:bg-white/5 hover:text-white">Cancel</Button>
      </div>
    </div>
  );
}

function SubmissionCard({ s, now, highlighted, onApprove, onDecline, busyId }) {
  const [declining, setDeclining] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const Icon = PLATFORM_ICONS[s.platform] || Video;
  const busy = busyId === s.id;
  const grantPending = s.status === 'approved' && !s.credit_granted_at;
  return (
    <li id={`earn-${s.id}`} className={cn(CARD, 'p-5', highlighted && 'ring-1 ring-brand-500/60')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5">
            <Icon className="h-4 w-4 text-white/80" />
          </span>
          <div className="min-w-0">
            <p className="text-[15px] text-white">{s.name} · {formatEarnAmount(s.amount_cents)}</p>
            <p className="truncate text-[12px] text-white/55">
              {s.customer?.first_name || 'Customer'} · {s.customer?.email || s.user_id} · {s.customer?.trips ?? 0} trip{s.customer?.trips === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {s.status === 'pending' && <DueBadge dueAt={s.due_at} now={now} />}
          <span className="text-[11px] text-white/40">Sent {fmtWhen(s.created_at)}</span>
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-[1fr_auto]">
        <div className="space-y-2 text-[13px]">
          <p className="text-white/80">Handle: <span className="text-white">@{s.handle || '(removed)'}</span></p>
          {s.post_url
            ? <a href={s.post_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 break-all text-brand-200 hover:text-white">{s.post_url} <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" /></a>
            : <p className="text-white/50">No link: screenshots only.</p>}
          {s.note && <p className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-white/75">“{s.note}”</p>}
          {s.status === 'pending' && (
            <ul className="space-y-1 pt-1 text-[12px] text-white/55">
              <li>☐ “{s.disclosure_label}” label is on the post, and #ad is in the caption</li>
              <li>☐ Posted from @{s.handle}, public, and it’s their own video</li>
              <li>☐ It shows or says Defera</li>
              <li className="text-white/40">Don’t judge whether it’s positive: honest posts earn the same.</li>
            </ul>
          )}
        </div>
        {s.screenshots?.length > 0 && (
          <div className="flex gap-2">
            {s.screenshots.map((url, i) => (
              <a key={url} href={url} target="_blank" rel="noreferrer" className="block h-28 w-20 overflow-hidden rounded-lg border border-white/15 hover:border-white/35">
                <img src={url} alt={`Screenshot ${i + 1}`} className="h-full w-full object-cover" />
              </a>
            ))}
          </div>
        )}
      </div>

      {s.status === 'pending' && !declining && (
        <div className="mt-4 flex flex-wrap gap-2">
          {confirming ? (
            <>
              <Button type="button" size="sm" disabled={busy} onClick={() => onApprove(s)} className={cn('rounded-lg', BRAND_PRIMARY_BTN_CLASS)}>
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : `Confirm: add ${formatEarnAmount(s.amount_cents)}`}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setConfirming(false)} className="rounded-lg text-white/60 hover:bg-white/5 hover:text-white">Cancel</Button>
            </>
          ) : (
            <>
              <Button type="button" size="sm" onClick={() => setConfirming(true)} className={cn('rounded-lg', BRAND_PRIMARY_BTN_CLASS)}>
                <CircleCheck className="mr-1.5 h-3.5 w-3.5" /> Approve and add {formatEarnAmount(s.amount_cents)}
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setDeclining(true)} className="rounded-lg border-white/15 bg-white/5 text-white/80 hover:bg-white/10">
                <CircleX className="mr-1.5 h-3.5 w-3.5" /> Decline
              </Button>
            </>
          )}
        </div>
      )}
      {declining && <DeclinePicker busy={busy} onCancel={() => setDeclining(false)} onConfirm={(reason) => onDecline(s, reason)} />}

      {s.status !== 'pending' && (
        <div className="mt-4 border-t border-white/[0.06] pt-3 text-[12px] text-white/55">
          {s.status === 'approved' && !grantPending && <span className="text-emerald-200">Credit added {fmtWhen(s.credit_granted_at)}</span>}
          {grantPending && (
            <span className="flex flex-wrap items-center gap-2 text-amber-200">
              Approved, but the credit didn’t go through.
              <Button type="button" size="sm" disabled={busy} onClick={() => onApprove(s)} className={cn('h-7 rounded-lg text-xs', BRAND_PRIMARY_BTN_CLASS)}>
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Retry adding the credit'}
              </Button>
            </span>
          )}
          {s.status === 'rejected' && <span>Declined {fmtWhen(s.reviewed_at)}: “{s.review_reason}”</span>}
          {s.status === 'withdrawn' && <span>Withdrawn by the customer</span>}
        </div>
      )}
    </li>
  );
}

export default function EarnCreditReview() {
  const location = useLocation();
  const focusId = useMemo(() => new URLSearchParams(location.search).get('id'), [location.search]);
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const scrolled = useRef(false);

  useEffect(() => {
    let cancelled = false;
    UserEntity.me().then((me) => { if (!cancelled) setUser(me || null); }).catch(() => {}).finally(() => { if (!cancelled) setChecked(true); });
    return () => { cancelled = true; };
  }, []);

  const admin = isCurrentUserAdmin(user);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await earnCreditApi.queue(status));
      setNow(Date.now());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { if (admin) load(); }, [admin, load]);

  useEffect(() => {
    if (!focusId || scrolled.current || !data?.submissions?.some((s) => s.id === focusId)) return;
    scrolled.current = true;
    document.getElementById(`earn-${focusId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [data, focusId]);

  const act = async (s, run, done) => {
    setBusyId(s.id);
    setNotice('');
    setError('');
    try {
      const result = await run();
      setNotice(done(result));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
      load();
    }
  };

  if (!checked) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-white/40" /></div>;
  if (!admin) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <div className={`${CARD} max-w-sm p-8 text-center`}>
          <ShieldAlert className="mx-auto mb-3 h-6 w-6 text-white/40" />
          <h1 className="mb-1 text-lg font-semibold text-white">Admin only</h1>
          <p className="text-sm text-white/55">Earn Defera Credit submissions are reviewed by the Defera team.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Earn Credit Review · Defera</title></Helmet>
      <div className="mx-auto min-h-screen max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-white">Earn Credit review</h1>
            <p className="mt-1.5 text-sm text-white/50">Customers were promised a review within {EARN_CREDIT_REVIEW_WINDOW}. Oldest first.</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={load} disabled={loading} className="rounded-lg border-white/15 bg-white/5 text-white/80 hover:bg-white/10">
            <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', loading && 'animate-spin')} /> Refresh
          </Button>
        </div>

        <div className="mb-5 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.status}
              type="button"
              onClick={() => setStatus(t.status)}
              className={cn(
                'rounded-full border px-3.5 py-1.5 text-[13px] transition-colors',
                status === t.status ? 'border-white/30 bg-white/10 text-white' : 'border-white/10 text-white/55 hover:text-white/85',
              )}
            >
              {t.label}{t.status === 'pending' && data ? ` · ${data.pending_count}` : ''}
            </button>
          ))}
        </div>

        {notice && <p role="status" className="mb-4 rounded-xl border border-emerald-400/25 bg-emerald-400/10 px-4 py-2.5 text-[13px] text-emerald-100">{notice}</p>}
        {error && <p role="alert" className="mb-4 rounded-xl border border-rose-400/25 bg-rose-400/10 px-4 py-2.5 text-[13px] text-rose-100">{error}</p>}

        {!data && loading && <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-white/40" /></div>}
        {data && data.submissions.length === 0 && (
          <div className={`${CARD} p-10 text-center text-sm text-white/50`}>
            {status === 'pending' ? 'Nothing waiting. You’re all caught up.' : 'Nothing here yet.'}
          </div>
        )}
        {data && data.submissions.length > 0 && (
          <ul className="space-y-4">
            {data.submissions.map((s) => (
              <SubmissionCard
                key={s.id}
                s={s}
                now={now}
                highlighted={s.id === focusId}
                busyId={busyId}
                onApprove={(sub) => act(sub, () => earnCreditApi.approve(sub.id), () => `Added ${formatEarnAmount(sub.amount_cents)} to ${sub.customer?.first_name || 'the customer'}'s Defera Credit. They'll get the credit email.`)}
                onDecline={(sub, reason) => act(sub, () => earnCreditApi.decline(sub.id, reason), (r) => (r?.emailed ? 'Declined, and the customer was emailed the reason.' : 'Declined. The reason is on their Earn page (the email didn’t send).'))}
              />
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
